import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  ActionPermissions,
  CustomPermission,
  FormFieldItem,
  FormObject,
  InterceptedFormsObject,
} from '../../../@types';
import { isUserHasPermission } from '../../../configs/permissions';
import { blockInteractionAvailable } from '../../../utils/interactions';
import { FormInterceptorContext } from '../../auth/AxiosInterceptor';

export interface UseCreateServiceInteractionsArgs {
  permissions: CustomPermission[];
  resetTrigger: string;
}

export interface PrepareSubmissionResult {
  ok: boolean;
  forms: FormObject[];
  originalPayload: unknown;
  missingCount: number;
}

const fieldHasValue = (field: FormFieldItem): boolean => {
  if (field.value === undefined || field.value === null) return false;
  if (typeof field.value === 'string') return field.value.trim() !== '';
  if (typeof field.value === 'boolean') return field.value;
  if (Array.isArray(field.value)) return field.value.length > 0;
  return true;
};

const countUnfilledMandatory = (forms: FormObject[]): number =>
  forms.reduce(
    (count, form) =>
      count +
      form.containers.reduce(
        (cAcc, container) =>
          cAcc +
          container.fields.filter(
            (field) => (field.isRequired || form.isMandatory) && !fieldHasValue(field)
          ).length,
        0
      ),
    0
  );

export interface UseCreateServiceInteractionsResult {
  forms: FormObject[];
  hasForms: boolean;
  mandatoryCount: number;
  unfilledMandatoryCount: number;
  blocked: boolean;
  serviceId: number | undefined;
  setServiceId: (id: number | undefined) => void;
  updateForm: (idx: number, form: FormObject) => void;
  reset: () => void;
  prepareSubmission: () => PrepareSubmissionResult;
  intercepted: InterceptedFormsObject | undefined;
}

/**
 * Manages the post-submit interaction-form state for the Create Service page.
 *
 * The Axios response interceptor populates `FormInterceptorContext` whenever
 * the BFF returns mandatory interaction forms tied to the chosen priority.
 * This hook surfaces those forms, lets callers patch individual entries as the
 * user fills them in, and validates them before the bypass-resubmit path.
 */
export const useCreateServiceInteractions = ({
  permissions,
  resetTrigger,
}: UseCreateServiceInteractionsArgs): UseCreateServiceInteractionsResult => {
  const { interceptedFormsObject, setInterceptedFormsObject } = useContext(FormInterceptorContext);
  const [forms, setForms] = useState<FormObject[]>([]);
  const [serviceId, setServiceId] = useState<number | undefined>(undefined);

  // Sync local form state when the context fills in (after the first submit).
  useEffect(() => {
    if (interceptedFormsObject?.forms?.length) {
      setForms(interceptedFormsObject.forms);
    } else {
      setForms([]);
    }
  }, [interceptedFormsObject]);

  // Clear any stale forms when the user changes the inputs that drive which
  // interactions apply — e.g. picking a different priority. Skip the first
  // run so we don't wipe forms that were already in the context on mount.
  // `interceptedFormsObject` is deliberately not in deps: this effect is keyed
  // on resetTrigger only, since adding the context value would re-fire (and
  // wipe) every time the BFF populated it after submit. The read of the
  // current context value is just a guard to avoid a redundant setter call.
  const initialReset = useRef(true);
  useEffect(() => {
    if (initialReset.current) {
      initialReset.current = false;
      return;
    }
    if (interceptedFormsObject) setInterceptedFormsObject(undefined);
    setForms([]);
    setServiceId(undefined);
  }, [resetTrigger, setInterceptedFormsObject]);

  const reset = useCallback(() => {
    setForms([]);
    setServiceId(undefined);
    setInterceptedFormsObject(undefined);
  }, [setInterceptedFormsObject]);

  const updateForm = useCallback((idx: number, form: FormObject) => {
    setForms((prev) => {
      const next = [...prev];
      next[idx] = form;
      return next;
    });
  }, []);

  const mandatoryCount = useMemo(() => forms.filter((f) => f.isMandatory).length, [forms]);

  const blocked = useMemo(
    () => blockInteractionAvailable(permissions, forms),
    [permissions, forms]
  );

  const canBypass = useMemo(
    () =>
      isUserHasPermission(ActionPermissions.Configure_Interactions_Bypass_Mandatory, permissions),
    [permissions]
  );

  const unfilledMandatoryCount = useMemo(
    () => (canBypass ? 0 : countUnfilledMandatory(forms)),
    [canBypass, forms]
  );

  const prepareSubmission = useCallback((): PrepareSubmissionResult => {
    if (!interceptedFormsObject) {
      return { ok: false, forms, originalPayload: undefined, missingCount: 0 };
    }
    const missingCount = canBypass ? 0 : countUnfilledMandatory(forms);
    return {
      ok: missingCount === 0,
      forms,
      originalPayload: interceptedFormsObject.payload,
      missingCount,
    };
  }, [interceptedFormsObject, forms, canBypass]);

  return {
    forms,
    hasForms: forms.length > 0,
    mandatoryCount,
    unfilledMandatoryCount,
    blocked,
    serviceId,
    setServiceId,
    updateForm,
    reset,
    prepareSubmission,
    intercepted: interceptedFormsObject,
  };
};
